import { Skeleton } from '@mantine/core';
import { useMemo, useState } from 'react';
import { api } from '~/trpc-extra/api';
import PlaywrightTraceViewer from '../trace/PlaywrightTraceViewer';
import type { GetTestRunAttemptType } from '~/server/api/routers/testRun';
import TestRunAttemptSwitcher from './TestRunAttemptSwitcher';

// Loading skeleton for the replay tab
const ReplayTabSkeleton = () => (
  <div className="space-y-4">
    <div className="flex items-start justify-between space-x-6">
      <div className="flex flex-col space-y-1">
        <Skeleton
          height={20}
          width={120}
          radius="md"
        />
        <Skeleton
          height={16}
          width={80}
          radius="md"
        />
      </div>
      <Skeleton
        height={30}
        width={80}
        radius="md"
      />
    </div>
    <Skeleton
      height="70vh"
      radius="md"
    />
  </div>
);

type TestRunReplayTabProps = {
  runId: string;
  isFullView?: boolean;
};

export default function TestRunReplayTab({ runId, isFullView }: TestRunReplayTabProps) {
  const { data: testRun, isLoading: isTestRunLoading } = api.testRun.get.useQuery({ runId });
  const [selectedTestRunAttempt, setSelectedTestRunAttempt] = useState<GetTestRunAttemptType | undefined>();
  const testRunAttempts = testRun?.TestRunAttempts || [];

  const traceIdentifier = useMemo(
    () =>
      selectedTestRunAttempt && !selectedTestRunAttempt.isManual && testRun ?
        {
          projectId: testRun.testV2.projectId,
          testId: testRun.test.id,
          testRunId: testRun.id,
          attemptId: selectedTestRunAttempt.id,
        }
      : undefined,
    [selectedTestRunAttempt, testRun],
  );

  if (isTestRunLoading) {
    return <ReplayTabSkeleton />;
  }

  if (!testRun) {
    return <div className="mt-4">No trace available.</div>;
  }

  return (
    <>
      {testRun.status !== 'SKIPPED' && (
        <TestRunAttemptSwitcher
          testRunAttempts={testRunAttempts}
          isFullView={isFullView}
          onAttemptChange={setSelectedTestRunAttempt}
          runStatus={testRun.status}
          traceIdentifier={traceIdentifier}
        />
      )}

      {traceIdentifier ?
        <PlaywrightTraceViewer
          className="w-full flex-1"
          trace={{
            type: 'identifier',
            traceIdentifier,
          }}
          ready={selectedTestRunAttempt?.status !== 'RUNNING'}
        />
      : <div className="mt-4">No trace available.</div>}
    </>
  );
}
